// @flow

import type { Dictionary } from './types';
import { serviceFactors } from './mechanical-factors';
import { spaceDescriptions, xbkBushings } from './thermal-factors';

import flatMap from 'lodash/fp/flatMap';
import flow from 'lodash/fp/flow';
import reduce from 'lodash/fp/reduce';

type SpecMsg = ((title: string) => string | string);
type SpecValues = { [string]: mixed };
type Validator = (value: number, specs: SpecValues) => boolean;

type Inputs = {
  [string]: Array<{|
    abbr?: string,
    hidden?: boolean,
    info?: string,
    level?: 'warning' | 'error',
    message?: string,
    notes?: string,
    spec: string,
    title: string,
    validator?: Validator,
    values?: Dictionary
  |}>
}

// TODO: move to utils
const isNumeric = function(value: mixed): boolean {
  value = parseFloat(value);
  return !isNaN(value) && isFinite(value);
};

const defaultValidator = function(value: mixed): boolean {
  return isNumeric(value) && parseFloat(value) > 0;
};

const defaultMessage = function(title: string): string {
  return `${title} must be a number`;
};

const lteq = function(value: mixed, max: number): boolean {
  value = parseFloat(value);
  return defaultValidator(value) && value <= max;
};

const inputs: Inputs = {
  mechanical: [
    {
      title: 'Tons per hour',
      spec: 'tonsPerHour',
      abbr: 'tn',
      validator: function(value: mixed): boolean {
        return lteq(value, 5000);
      },
      message: 'should be between 0–5000 tons/hr'
    },
    {
      title: 'Length of Conveyor',
      spec: 'conveyorLength',
      abbr: 'ft',
      validator: function(value: mixed): boolean {
        return lteq(value, 1000);
      },
      message: 'should be between 0–1000 ft'
    },
    {
      title: 'Conveyor Speed',
      spec: 'conveyorSpeed',
      abbr: 'fpm',
      validator: function(value: mixed): boolean {
        return lteq(value, 1000);
      },
      message: 'should be between 0–1000 fpm'
    },
    {
      title: 'Conveyor Rise',
      spec: 'conveyorRise',
      abbr: 'ft',
      validator: function(value: mixed, values: SpecValues): boolean {
        value = parseFloat(value);
        const isValid = value >= 0;
        if (!values['conveyorLength']) {
          return isValid;
        }
        // It actually should be if the rise becomes more than 50% of the run. (which is 30 degrees)
        // Which 25-30 degrees is often the angle of repose for gravel.
        const max = parseFloat(values['conveyorLength']) * 0.5;
        return isValid && value <= max;
      },
      message: 'should be from 0 to less than 50% of conveyor length'
    },
    {
      title: 'Head Pulley Diameter',
      spec: 'headPulleyDiameter',
      abbr: 'in',
      validator: function(value: mixed): boolean {
        return lteq(value, 60);
      },
      message: 'should not exceed 60 in'
    },
    {
      title: 'Head Pulley Shaft Diameter',
      spec: 'headPulleyShaftDiameter',
      values: xbkBushings,
      validator: function(value: mixed): boolean {
        return isNumeric(value) && parseFloat(value) >= 0;
      },
      hidden: true
    },
    {
      title: 'Service Factor',
      spec: 'serviceFactor',
      values: serviceFactors
    },
    {
      title: 'Motor Speed',
      spec: 'motorSpeed',
      abbr: 'rpm',
      validator: function(value: mixed): boolean {
        return lteq(value, 3600);
      },
      message: 'should not exceed 3600 typically'
    }
  ],
  thermal: [
    {
      title: 'Ambient Temp',
      spec: 'ambientTemp',
      abbr: '°F',
      validator: function(value: mixed): boolean {
        value = parseFloat(value);
        return isNumeric(value) && value >= -20 && value <= 120;
      },
      message: 'must be between -20°F and 120°F',
      level: 'error'
    },
    {
      title: 'Duty Cycle',
      spec: 'dutyCycle',
      abbr: '%',
      validator: function(value: mixed): boolean {
        return lteq(value, 100);
      },
      message: 'must be between 0–100%',
      level: 'error',
      info: 'Operating percentage per hour, ex: runs for 2 straight hours per day = 100% duty cycle. Must be between 0–100%'
    },
    {
      title: 'Altitude',
      spec: 'altitude',
      abbr: 'ft',
      validator: function(value: mixed): boolean {
        value = parseFloat(value);
        return isNumeric(value) && value >= 0 && value <= 17200;
      },
      message: 'must be between 0—17,200 ft',
      level: 'error'
    },
    {
      title: 'Space Description',
      spec: 'spaceDescription',
      values: spaceDescriptions
    }
  ]
};

const defaultValues = flow(
  flatMap(x => x),
  reduce((values, y) => {
    if (y.values) {
      const key = Object.keys(y.values)[0];
      values[y.spec] = `${y.values[key]}: ${key}`;
    }
    else if (y.hasOwnProperty('defaultValue')) {
      values[y.spec] = y.defaultValue;
    }
    else {
      values[y.spec] = null;
    }
    return values;
  }, {})
)(inputs);

const validators = flow(
  flatMap(x => x),
  reduce((values, y) => {
    values[y.spec] = {
      validate: y.validator || defaultValidator,
      message: function() {
        let message: SpecMsg = y.message || defaultMessage;
        if (typeof message === 'string') {
          return message;
        }
        return message(y.title);
      },
      level: y.level || 'warning'
    };
    return values;
  }, {})
)(inputs);

export {
  defaultMessage,
  defaultValidator,
  defaultValues,
  isNumeric,
  validators
};

export default inputs;



// WEBPACK FOOTER //
// ./src/utils/vortex/inputs.js